import React from 'react';
import {
    Responsive,
    SimpleList,
    Datagrid,
    SimpleShowLayout,
    SimpleForm,
    BooleanField,
    TextField,
    LongTextInput,
    NumberInput,
    SelectInput,
    TextInput,
    required,
    maxValue,
    minValue
} from 'admin-on-rest';
import List from '../admin/List';
import Create from '../admin/Create';
import Show from '../admin/Show';
import Edit from '../admin/Edit';
import ViewButton from '../buttons/ViewButton';
import EditButton from '../buttons/EditButton';
import {viewStyles} from '../shared/styles-view';
import Avatar from 'material-ui/Avatar';
import Icon from 'material-ui/svg-icons/editor/attach-money';
import TooltipIcon from '../components/TooltipIcon';
import ReactTooltip from 'react-tooltip';

export const fuseLocationAdditionalTaxIcon = Icon;

const styles = {
    fieldSmall: {flex: '0 1 10%', display: 'inline-block', maxWidth: '8.5em', overflow: 'hidden'},
};

/*
 * View: List
 */

const ListTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                                className="avatar-title"/>Additional Taxes</span>;

const ListDescription = () => {
    return (
        <span>
            <p>Some <strong>DBAs</strong> and <strong>Locations</strong> need one or
                more <strong>Additional Taxes</strong> applied depending on state law.</p>
        </span>
    );
};

/*
 * List Additional Taxes tables (Full and View Only)
 */
export const fuseLocationAdditionalTaxList = (props) => (
    <List {...props} title={ListTitle} desc={<ListDescription/>} sort={{field: 'name', order: 'ASC'}}>
        <Responsive
            small={
                <SimpleList
                    primaryText={record => record.name}
                    secondaryText={record => record.tax_rate}
                    tertiaryText={record => record.description}
                />
            }
            medium={
                <Datagrid bodyOptions={{stripedRows: false, showRowHover: true}}>
                    <TextField source="name" label="Name"/>
                    <TextField source="description" label="Description"/>
                    <TextField source="tax_rate_percent" label="tax Rate"/>
                    <ViewButton/>
                    <EditButton/>
                </Datagrid>
            }
        />
    </List>
);

export const fuseLocationAdditionalTaxListViewOnly = (props) => (
    <List {...props} title={ListTitle} desc={<ListDescription/>} sort={{field: 'name', order: 'ASC'}}>
        <Responsive
            small={
                <SimpleList
                    primaryText={record => record.name}
                    secondaryText={record => record.tax_rate}
                    tertiaryText={record => record.description}
                />
            }
            medium={
                <Datagrid bodyOptions={{stripedRows: false, showRowHover: true}}>
                    <TextField source="name" label="Name"/>
                    <TextField source="description" label="Description"/>
                    <TextField source="tax_rate_percent" label="tax Rate"/>
                    <ViewButton/>
                </Datagrid>
            }
        />
    </List>
);

/*
 * View: Show
 */

const ShowTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                         className="avatar-title"/>{record ? `${record.name}` : 'Additional Tax'}</span>;
};

const ShowDescription = () => {
    return (
        <span>
            <p>Review Additional Tax details below. To edit this Additional Tax,
                click <strong>Edit</strong> above.</p>
        </span>
    );
};

/*
 * Show Additional Tax Table
 */
export const fuseLocationAdditionalTaxShow = (props) => (
    <Show title={<ShowTitle/>} desc={<ShowDescription/>} {...props}>
        <SimpleShowLayout>
            <TextField source="machine_name" label="ID"/>
            <TextField source="name" label="Name of Additional Tax"/>
            <TextField source="description" label="Description"/>
            <TextField source="tax_rate_percent" label="Tax Rate"/>
            <BooleanField source="active" label="Tax Rate is Active" elStyle={styles.fieldSmall}/>
        </SimpleShowLayout>
    </Show>
);

const validateName = (value) => {
    let error = false;
    // Name fields may contain only alphanumeric characters, spaces, hyphens, apostrophes
    if (/[^A-Za-z0-9- ']/.test(value)) {
        error = 'Name must not contain special characters';
    }
    return error;
};

/*
 * View: Create form
 */

const CreateTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                                  className="avatar-title"/>Create an Additional Tax</span>;

const CreateDescription = () => {
    return (
        <span>
            <p>Some <strong>DBAs</strong> and <strong>Locations</strong> need one or
                more <strong>Additional Taxes</strong> applied depending on state law.</p>
            <p>Enter the required fields in the tabs below.</p>
        </span>
    );
};

const defaultCreateValues = {
    tax_rate: 1
};

/*
 * Create Additional Tax form
 */
export const fuseLocationAdditionalTaxCreate = (props) => (
    <Create {...props} title={CreateTitle} desc={<CreateDescription/>}>
        <SimpleForm redirect="list" defaultValue={defaultCreateValues}>
            <TextInput source="name" label="Name of Additional Tax" validate={[required, validateName]}/>
            <LongTextInput source="description" label="Description of Additional Tax"/>
            <NumberInput source="tax_rate" label="Tax Rate %"
                         step={1}
                         validate={[required, minValue(0, 'Tax Rate must be a positive number.'), maxValue(100, 'Tax Rate cannot be larger than 100%.')]}
            />
            <span style={viewStyles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the Additional Taxes List.
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
        </SimpleForm>
    </Create>
);

/*
 * View: Edit form
 */

const EditTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                         className="avatar-title"/>Edit {record ? `${record.name}` : 'Additional Tax'}</span>;
};

const EditDescription = () => {
    return (
        <span>
            <p>Modify Additional Tax fields in the tabs below. Click <strong>Save</strong> to store this
                Additional Tax's details.</p>
        </span>
    );
};

/*
 * Edit Additional Tax form
 */
export const fuseLocationAdditionalTaxEdit = (props) => (
    <Edit {...props} title={<EditTitle/>} desc={<EditDescription/>}>
        <SimpleForm>
            <TextField source="machine_name" label="ID"/>
            <TextInput source="name" label="Name of Additional Tax" validate={[required, validateName]}/>
            <LongTextInput source="description" label="Description of Additional Tax"/>
            <NumberInput source="tax_rate" label="Tax Rate %"
                         step={1}
                         validate={[required, minValue(0, 'Tax Rate must be a positive number.'), maxValue(100, 'Tax Rate cannot be larger than 100%.')]}
            />
            <SelectInput source="active" label="Tax Rate is Active"
                         validate={[required]}
                         choices={[
                             {id: true, name: 'Active'},
                             {id: false, name: 'Inactive'},
                         ]}/>
            <span style={viewStyles.fieldDescription}>
                Select Active or Inactive.
                <TooltipIcon dataFor="ttActive"/>
                <ReactTooltip id="ttActive" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    <em>Active</em> Additional Tax Rates are applied for a Location or DBA on the POS System.
                    <br/>
                    <em>Inactive</em> Additional Tax Rates are not applied for a Location or DBA on the POS System.
                </ReactTooltip>
            </span>

            <span style={viewStyles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the Additional Taxes List.
            </span>
            <hr style={{...viewStyles.break, ...styles.paddingBottom}}/>
        </SimpleForm>
    </Edit>
);



// WEBPACK FOOTER //
// ./src/Fuse/Location/additionalTax.js